/**
 * Stream CLAIMS over the one push channel.
 *
 * `PushChannel.subscribe()` REPLACES the whole subscription set (web-kit
 * `push/channel.ts`), and this app wires exactly ONE channel (`main.ts`). So two
 * surfaces that are mounted together and each call `subscribe([…their own streams])`
 * silently clobber each other: the last caller wins and the first surface goes deaf
 * without any error — the »my other tab updated but this one did not« class of bug.
 *
 * The fix is a ref-counted registry in front of the channel. A surface ACQUIRES the
 * streams it needs and gets a `release()` back; every acquire/release re-subscribes
 * the channel with the UNION of all live claims. Nobody calls `push.subscribe()`
 * directly any more.
 *
 * Claims are keyed by channel instance (a `WeakMap`), so tests with their own fake
 * channels never share a registry with each other or with the app.
 *
 * Note on the empty union: web-kit's `subscribe([])` records the empty set (so the
 * next claim re-opens correctly) but leaves the current EventSource open until the
 * next `resume()`. That is the channel's business, not this helper's — the frames it
 * still delivers fold idempotently and change nothing.
 */

import { effect, type PushChannel, type ReadonlySignal } from "@platform/web-kit"

/** Live claims per channel: streamId → how many holders want it. */
const registry = new WeakMap<PushChannel, Map<string, number>>()

const claimsOf = (push: PushChannel): Map<string, number> => {
  let counts = registry.get(push)
  if (!counts) {
    counts = new Map<string, number>()
    registry.set(push, counts)
  }
  return counts
}

/**
 * Claim `ids` on the shared channel; the returned `release()` gives them back.
 * Both directions re-subscribe with the union of every live claim, so a surface can
 * never take another surface's streams away. `release()` is idempotent.
 */
export function acquireStreams(push: PushChannel, ids: readonly string[]): () => void {
  const counts = claimsOf(push)
  const mine = [...new Set(ids)]
  for (const id of mine) counts.set(id, (counts.get(id) ?? 0) + 1)
  push.subscribe([...counts.keys()])

  let released = false
  return () => {
    if (released) return
    released = true
    for (const id of mine) {
      const next = (counts.get(id) ?? 1) - 1
      if (next > 0) counts.set(id, next)
      else counts.delete(id)
    }
    push.subscribe([...counts.keys()])
  }
}

/** A claim that can be re-cut while the surface stays mounted. */
export interface StreamClaim {
  /** Replace this holder's claim with `ids` (the union is re-subscribed). */
  claim(ids: readonly string[]): void
  /** Give up everything this holder claimed. Idempotent. */
  release(): void
}

/**
 * For surfaces whose stream set GROWS while they are mounted (a search run starts, a
 * generation is kicked off): one holder, re-claimed as the set changes, released once
 * on detach.
 */
export function createStreamClaim(push: PushChannel): StreamClaim {
  let release: (() => void) | null = null
  return {
    claim(ids: readonly string[]): void {
      const previous = release
      release = acquireStreams(push, ids)
      previous?.() // AFTER the new claim, so a shared stream never drops to zero in between
    },
    release(): void {
      release?.()
      release = null
    },
  }
}

/**
 * Run `fn` when `dep` CHANGES — not on registration. `effect` fires an initial run by
 * design, but a push-driven re-read must not duplicate the fetch the surface's own
 * `attach()` already issued.
 */
export function onChange(dep: ReadonlySignal<unknown>, fn: () => void): () => void {
  let first = true
  return effect([dep], () => {
    if (first) {
      first = false
      return
    }
    fn()
  })
}
